import { FC } from 'react';
import { useQueryClient } from 'react-query';
import { DateTime } from 'luxon';
import { ButtonLink, ButtonPrimary } from '@lambdacurry/component-library';
import { OrdersQuery, SubscriptionOrder } from 'lib/home/generated/types/api';
import { useUpdateOrderMutation } from 'lib/home/generated/queries';
import { Card, CardContent, Grid } from 'lib/components';
import { default as UpcomingBoxPattern } from './UpcomingBoxPattern.svg';
import { formatDate } from 'lib/util/date';

interface UpcomingOrdersProps {
  upcomingOrders: SubscriptionOrder[];
  subscriptionTitle: string;
}

export const UpcomingOrders: FC<UpcomingOrdersProps> = ({ upcomingOrders, subscriptionTitle }) => {
  const queryClient = useQueryClient();
  const { mutate } = useUpdateOrderMutation({
    onSuccess: ({ updateSubscriptionOrder }) => {
      queryClient.setQueryData(['Orders'], (query: OrdersQuery) => {
        const upcomingOrderIndex = query.me.subscription.upcoming_orders.findIndex(
          order => order.id === updateSubscriptionOrder.id
        );
        query.me.subscription.upcoming_orders[upcomingOrderIndex] = updateSubscriptionOrder;
        return query;
      });
    }
  });

  if (!upcomingOrders) return null;

  return (
    <div className="my-8">
      <h2>Upcoming Boxes</h2>

      <Grid className="mt-8">
        {upcomingOrders.map(order =>
          order.skip ? (
            <Card
              key={order.id}
              className="upcoming-order col-span-12 sm:col-span-6 lg:col-span-4 bg-gray-100 border border-gray-200"
            >
              <CardContent variant="condensed" className="flex items-center justify-between gap-4 h-[103px]">
                <div>
                  <h5 className="m-0">{formatDate(DateTime.fromISO(order.complete_order_at), 'LLLL dd')}</h5>
                  <p className="m-0">SKIPPED</p>
                </div>

                <ButtonPrimary
                  data-cy="unskip"
                  onClick={() => mutate({ updateSubscriptionOrderInput: { id: order.id, skip: false } })}
                >
                  Unskip
                </ButtonPrimary>
              </CardContent>
            </Card>
          ) : (
            <Card
              key={order.id}
              className="upcoming-order col-span-12 sm:col-span-6 lg:col-span-4 relative border border-brand-text-50"
            >
              <CardContent variant="condensed" className="flex items-center justify-between gap-4 h-[103px]">
                <div>
                  <h5 className="m-0">{formatDate(DateTime.fromISO(order.complete_order_at), 'LLLL dd')}</h5>
                  <p className="m-0">
                    {subscriptionTitle}
                    <ButtonLink
                      data-cy="skip"
                      className="!underline !text-base !h-auto ml-2"
                      onClick={() => mutate({ updateSubscriptionOrderInput: { id: order.id, skip: true } })}
                    >
                      SKIP
                    </ButtonLink>
                  </p>
                </div>

                <div className="bg-brand-cool-green-700 absolute top-0 right-0 h-full">
                  <UpcomingBoxPattern className="h-full" />
                </div>
              </CardContent>
            </Card>
          )
        )}
      </Grid>
    </div>
  );
};
